import React from 'react';
import { string, shape, bool, arrayOf, number, func } from 'prop-types';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import { nanoid } from 'nanoid';
import moment from 'moment-timezone';

import { ORDER_STATUS, PAYMENT_METHODS } from '~constants/orders';
import { getDeliveryTime } from '~utils/time';
import { DELIVERY_TYPES } from '~constants/delivery';
import { MODALS } from '~redux/Modal/constants';
import DeliveryActions from '~redux/Delivery/actions';
import ModalActions from '~redux/Modal/actions';
import { DISPATCH_METHODS } from '~constants/orders';
import { snakeToCamel } from '~utils/string';
import { getFirstSuccessfulPayment } from '~utils/order';
import { intl } from '~lib/intl';

import { DELIVERY_ACTIONS_MODAL } from '../DeliveryActionsModal/constants';
import { useDeliveryStatus } from '../OrdersTableRow/hooks';
import { useRejectedOrder } from '../DeliveryActionsModal/useRejectedOrder';

import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import Summary from './components/Summary';
import PaymentMethod from './components/PaymentMethod';
import Title from './components/Title';
import ItemsAndDispatchMethod from './components/ItemsAndDispatchMethod';
import DiscountCoupon from './components/DiscountCoupon';
import { clientRows, priceRows, billingRows } from './constants';
import Delivery from './components/Delivery';
import ProofOfDelivery from './components/ProofOfDelivery';
import BillingDetails from './components/BillingDetails';

function OrderDetails({ item, setSelectedItems, showPrintable, isHistoryMode, isRejectedOrdersScreen }) {
  const {
    id,
    items,
    dispatchMethod,
    paymentMethod,
    payments,
    deliveries,
    forLater,
    laterStartTime,
    checkoutAnswers,
    channel,
    displayId,
    status,
    createdAt,
    store,
    storeName,
    fbdId
  } = item;

  const dispatch = useDispatch();
  const { disableDeliveryActions, isMercatChannel } = useDeliveryStatus(
    status,
    dispatchMethod,
    deliveries,
    channel,
    paymentMethod
  );
  const deliveryTime = getDeliveryTime(item);
  const SPOONITY_DEFAULT_NAME = 'Spooners cashback';
  const loyaltyProgramRaw = useSelector((state) => state.core?.settings?.loyaltyProgramParams);
  const spoonityLoyaltyProgram = useSelector((state) => state.core?.settings?.spoonityLoyaltyProgram);
  const loyaltyProgram = spoonityLoyaltyProgram
    ? { ...loyaltyProgramRaw, name: SPOONITY_DEFAULT_NAME }
    : loyaltyProgramRaw;
  const isAcceptedOrPreparing = status === ORDER_STATUS.PREPARING || status === ORDER_STATUS.CREATED;
  const isScheduled = status === ORDER_STATUS.SCHEDULED;
  const hasDelivery = deliveries?.length > 0;
  const hasOwnDelivery = isAcceptedOrPreparing && hasDelivery && deliveries[0].type === DELIVERY_TYPES.OWN;
  const { isRejected } = useRejectedOrder(hasDelivery, deliveries?.[0]?.status?.toLowerCase());
  const isDelivery = dispatchMethod === DISPATCH_METHODS.store_delivery;
  const proofOfDeliveryUrl = isDelivery && deliveries[deliveries.length - 1]?.proofOfDeliveryUrl;
  const isPaymentMethodOnline = paymentMethod === PAYMENT_METHODS.ONLINE;
  const { deliveryMethods = [] } = useSelector((state) => state.stores?.storeById);

  const showDeliveryActionButton =
    showPrintable &&
    !isHistoryMode &&
    !disableDeliveryActions &&
    isMercatChannel &&
    !isScheduled &&
    !isRejectedOrdersScreen &&
    (isRejected || !hasDelivery || hasOwnDelivery);

  const paymentName = i18next.t(
    `OrdersDetails:${isPaymentMethodOnline ? getFirstSuccessfulPayment(payments) : paymentMethod}`
  );

  const paymentMessage =
    isPaymentMethodOnline || !isMercatChannel
      ? i18next.t('OrderDetails:paid')
      : i18next.t('OrderDetails:pendingPayment');

  const handleDeliveryAction = () => {
    dispatch(ModalActions.closeModal(MODALS.ORDER_DETAILS));
    setSelectedItems([]);
    dispatch(
      DeliveryActions.setDeliveryItems({
        ...item,
        requestId: nanoid(),
        error: isRejected ? deliveries[0].status.toLowerCase() : null
      })
    );
    dispatch(ModalActions.openModal(DELIVERY_ACTIONS_MODAL));
  };

  return (
    <>
      <Title id={id} channel={channel} displayId={displayId} />
      {isRejected && !isHistoryMode && !isRejectedOrdersScreen && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleDeliveryAction}>
              {i18next.t('OrdersTableBody:assignDelivery')}
            </Button>
          }>
          <AlertTitle>
            {i18next.t('OrdersDetails:deliveryRejected', {
              status: i18next.t(`DeliveryStatus:${deliveries[0].status.toLowerCase()}`)
            })}
          </AlertTitle>
          {i18next.t('OrdersDetails:assignNewDelivery')}
        </Alert>
      )}
      <ItemsAndDispatchMethod
        dispatchMethod={dispatchMethod}
        itemsLength={items.length}
        deliveries={deliveries}
        item={item}
      />
      <ClientDetails clients={clientRows(item, dispatchMethod)} checkoutAnswers={checkoutAnswers || []} />
      {item.documentId ? <BillingDetails billing={billingRows(item)} /> : null}
      <Delivery
        dispatchMethod={dispatchMethod}
        deliveries={deliveries}
        forLater={forLater}
        laterStartTime={laterStartTime}
        deliveryTime={deliveryTime}
        checkoutAnswers={checkoutAnswers || []}
        channel={snakeToCamel(channel)}
        createdAt={moment(createdAt).tz(intl.timezone)}
        storeName={store?.name || storeName}
        posId={fbdId}
      />
      {showDeliveryActionButton && (
        <Button variant="outlined" onClick={handleDeliveryAction}>
          {hasOwnDelivery
            ? i18next.t('DeliveryActionsModal:title')
            : i18next.t('DeliveryActionsModal:noDeliveryTitle')}
        </Button>
      )}
      <ProductDetails items={items} />
      {item.coupon?.code && <DiscountCoupon code={item.coupon.code} />}
      <PaymentMethod channel={channel} paymentMethod={paymentName} paymentMessage={paymentMessage} />
      <Summary prices={priceRows(item, loyaltyProgram, deliveryMethods)} />
      {isHistoryMode && isDelivery ? <ProofOfDelivery imageLink={proofOfDeliveryUrl} /> : null}
    </>
  );
}

OrderDetails.propTypes = {
  isHistoryMode: bool,
  isRejectedOrdersScreen: bool,
  item: shape({
    id: number,
    items: arrayOf(shape({ id: number, name: string, price: number, quantity: number })),
    dispatchMethod: string,
    paymentMethod: string,
    payments: arrayOf(shape({ id: number, type: string, amount: number })),
    deliveries: arrayOf(shape({ id: number, status: string })),
    forLater: bool,
    laterStartTime: string
  }),
  setSelectedItems: func,
  showPrintable: bool
};

OrderDetails.defaultProps = {
  isRejectedOrdersScreen: false,
  showPrintable: true
};

export default OrderDetails;
